import React, { useRef, useEffect, useState } from 'react';
import { DollarSign, CreditCard, Tag, CheckCircle, AlertCircle, HelpCircle, Info } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext.tsx';

interface FinancialConfigFormProps {
  persistOnChange?: boolean;
  acceptsHealthInsurance: boolean;
  setAcceptsHealthInsurance: (val: boolean) => void;
  healthInsurancePlans: string;
  setHealthInsurancePlans: (val: string) => void;
  paymentMethods: string;
  setPaymentMethods: (val: string) => void;
  installmentConditions: string;
  setInstallmentConditions: (val: string) => void;
  evaluationPrice: string;
  setEvaluationPrice: (val: string) => void;
  treatmentPrices: string;
  setTreatmentPrices: (val: string) => void;
  // Nova prop para controle externo do checkbox "Valores sob avaliação"
  pricesUnderEvaluation?: boolean;
  setPricesUnderEvaluation?: (val: boolean) => void;
}

const InfoTooltip: React.FC<{ text: string }> = ({ text }) => (
  <span className="relative inline-flex group" tabIndex={0} aria-label={text}>
    <Info className="h-3.5 w-3.5 text-gray-400 transition-colors group-hover:text-sminy-primary" />
    <span
      role="tooltip"
      className="pointer-events-none absolute left-1/2 top-5 z-20 w-64 max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-xs font-normal leading-5 text-gray-700 opacity-0 shadow-xl transition-opacity group-hover:opacity-100 group-focus:opacity-100"
    >
      {text}
    </span>
  </span>
);

// Helper components (seguindo padrão perfeito)
const Field: React.FC<{ label: string; children: React.ReactNode; hint?: string; tooltip: string; required?: boolean }> = ({
  label,
  children,
  hint,
  tooltip,
  required = true
}) => {
  const { isDark } = useTheme();
  return (
    <label className="block text-sm">
      <span className={`mb-1 flex items-center gap-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'
        }`}>
        <span>{label}{required && <span className="text-red-500"> *</span>}</span>
        <InfoTooltip text={tooltip} />
      </span>
      {children}
      {hint && <span className={`mt-1 block text-[11px] ${isDark ? 'text-gray-400' : 'text-gray-500'
        }`}>{hint}</span>}
    </label>
  );
};

const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>((props, ref) => {
  const { isDark } = useTheme();
  return (
    <input
      ref={ref}
      {...props}
      className={`w-full rounded-xl border px-3 py-2 text-sm outline-none transition-all focus:ring-2 focus:ring-sminy-primary ${isDark
        ? 'border-gray-600 bg-gray-700 text-gray-200 placeholder:text-gray-400 focus:border-sminy-primary'
        : 'border-gray-300 bg-white text-gray-800 placeholder:text-gray-400 focus:border-sminy-primary'
        } ${props.className ?? ""}`}
    />
  );
});

const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>((props, ref) => {
  const { isDark } = useTheme();
  return (
    <textarea
      ref={ref}
      {...props}
      className={`w-full rounded-xl border px-3 py-2 text-sm outline-none transition-all focus:ring-2 focus:ring-sminy-primary ${isDark
        ? 'border-gray-600 bg-gray-700 text-gray-200 placeholder:text-gray-400 focus:border-sminy-primary'
        : 'border-gray-300 bg-white text-gray-800 placeholder:text-gray-400 focus:border-sminy-primary'
        } ${props.className ?? ""}`}
    />
  );
});

const Checkbox: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label: string; tooltip: string }> = ({
  checked,
  onChange,
  label,
  tooltip
}) => {
  const { isDark } = useTheme();
  return (
    <label className="inline-flex items-center gap-2 text-sm">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-gray-300 text-sminy-primary focus:ring-sminy-primary"
      />
      <span className={`flex items-center gap-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
        {label}
        <InfoTooltip text={tooltip} />
      </span>
    </label>
  );
};


const FinancialConfigForm: React.FC<FinancialConfigFormProps> = ({
  persistOnChange = false,
  acceptsHealthInsurance, setAcceptsHealthInsurance,
  healthInsurancePlans, setHealthInsurancePlans,
  paymentMethods, setPaymentMethods,
  installmentConditions, setInstallmentConditions,
  evaluationPrice, setEvaluationPrice,
  treatmentPrices, setTreatmentPrices,
  pricesUnderEvaluation = false,
  setPricesUnderEvaluation
}) => {
  const { isDark } = useTheme();

  // Estado para controlar se avaliação é gratuita
  // Detectar se o valor atual indica gratuito
  const detectFreeEvaluation = (price: string) => {
    const priceLower = price.toLowerCase().trim();
    return (
      priceLower === 'gratuito' ||
      priceLower === 'gratuita' ||
      priceLower.includes('gratuito') ||
      priceLower.includes('gratuita') ||
      priceLower.includes('sem custo') ||
      priceLower.includes('cortesia') ||
      priceLower === '' ||
      price === ''
    );
  };

  const [isFreeEvaluation, setIsFreeEvaluation] = useState(
    detectFreeEvaluation(evaluationPrice)
  );

  // Refs para inputs
  const plansRef = useRef<HTMLInputElement>(null);
  const paymentsRef = useRef<HTMLInputElement>(null);
  const installmentsRef = useRef<HTMLInputElement>(null);
  const evalPriceRef = useRef<HTMLTextAreaElement>(null);
  const treatPricesRef = useRef<HTMLTextAreaElement>(null);

  // Inicializar valores nos inputs
  useEffect(() => {
    if (plansRef.current) plansRef.current.value = healthInsurancePlans;
    if (paymentsRef.current) paymentsRef.current.value = paymentMethods;
    if (installmentsRef.current) installmentsRef.current.value = installmentConditions;
    if (evalPriceRef.current) {
      evalPriceRef.current.value = isFreeEvaluation ? 'Gratuito' : evaluationPrice;
    }
    if (treatPricesRef.current) treatPricesRef.current.value = treatmentPrices;
  }, [healthInsurancePlans, paymentMethods, installmentConditions, evaluationPrice, treatmentPrices, isFreeEvaluation]);

  // Atualizar valor quando checkbox muda
  useEffect(() => {
    if (isFreeEvaluation) {
      setEvaluationPrice('Gratuito');
      if (evalPriceRef.current) {
        evalPriceRef.current.value = 'Gratuito';
      }
    }
  }, [isFreeEvaluation, setEvaluationPrice]);

  // Handler para convênios que preserva scroll
  const handleHealthInsuranceChange = (checked: boolean) => {
    // Salvar posição atual do scroll
    const currentScrollY = window.scrollY;

    setAcceptsHealthInsurance(checked);

    // Manter posição de scroll após a atualização
    requestAnimationFrame(() => {
      window.scrollTo(0, currentScrollY);
    });
  };

  return (
    <div className="space-y-4">
      {persistOnChange && <h2 className="register-section-title">Convênios</h2>}
      {/* Convênios odontológicos */}
      <Checkbox
        checked={acceptsHealthInsurance}
        onChange={handleHealthInsuranceChange}
        label="Aceita convênios odontológicos"
        tooltip="Marque se a clínica atende por convênios. Se marcado, os nomes dos convênios serão obrigatórios."
      />

      {acceptsHealthInsurance && (
        <Field label="Convênios aceitos" tooltip="Liste os convênios que o assistente pode informar aos pacientes.">
          <Input
            ref={plansRef}
            type="text"
            required
            placeholder="Ex: Amil, SulAmérica, Bradesco"
            defaultValue={healthInsurancePlans}
            onChange={persistOnChange ? (e) => setHealthInsurancePlans(e.target.value) : undefined}
            onBlur={() => plansRef.current && setHealthInsurancePlans(plansRef.current.value)}
          />
        </Field>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Formas de pagamento" tooltip="Meios de pagamento aceitos pela clínica. Separe por vírgulas quando houver mais de um.">
          <Input
            ref={paymentsRef}
            type="text"
            required
            placeholder="Ex: Dinheiro, Cartão, Pix"
            defaultValue={paymentMethods}
            onChange={persistOnChange ? (e) => setPaymentMethods(e.target.value) : undefined}
            onBlur={() => paymentsRef.current && setPaymentMethods(paymentsRef.current.value)}
          />
        </Field>

        <Field label="Condições de parcelamento" tooltip="Regras de parcelamento que o assistente pode mencionar na conversa.">
          <Input
            ref={installmentsRef}
            type="text"
            required
            placeholder="Ex: Até 10x sem juros"
            defaultValue={installmentConditions}
            onChange={persistOnChange ? (e) => setInstallmentConditions(e.target.value) : undefined}
            onBlur={() => installmentsRef.current && setInstallmentConditions(installmentsRef.current.value)}
          />
        </Field>
      </div>

      <div className="space-y-3">
        <Checkbox
          checked={isFreeEvaluation}
          onChange={(checked) => {
            setIsFreeEvaluation(checked);
            if (!checked && evalPriceRef.current) {
              evalPriceRef.current.value = '';
              evalPriceRef.current.focus();
            }
          }}
          label="Avaliação gratuita"
          tooltip="Marque quando a primeira avaliação não tiver cobrança. O preço será preenchido como gratuito."
        />

        <Field
          label="Preço da avaliação"
          hint={isFreeEvaluation ? "A avaliação está marcada como gratuita" : "Informe o valor cobrado pela avaliação"}
          tooltip="Valor que o assistente poderá informar quando perguntarem sobre a avaliação."
        >
          <Textarea
            ref={evalPriceRef}
            rows={3}
            required
            placeholder={isFreeEvaluation ? "Gratuito" : "Ex: R$ 80,00 ou A partir de R$ 50,00"}
            defaultValue={isFreeEvaluation ? "Gratuito" : evaluationPrice}
            disabled={isFreeEvaluation}
            onChange={persistOnChange ? (e) => setEvaluationPrice(e.target.value) : undefined}
            onBlur={() => {
              if (evalPriceRef.current && !isFreeEvaluation) {
                setEvaluationPrice(evalPriceRef.current.value);
              }
            }}
            className={isFreeEvaluation ? 'opacity-60 cursor-not-allowed' : ''}
          />
        </Field>
      </div>

      {/* CHECKBOX INJETADO AQUI - ACIMA DO INPUT DE PREÇOS */}
      {setPricesUnderEvaluation && (
        <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={pricesUnderEvaluation}
              onChange={(e) => {
                setPricesUnderEvaluation(e.target.checked);
                if (e.target.checked) {
                  setTreatmentPrices("Os valores dos tratamentos são passados somente mediante avaliação");
                  // Se tivermos ref, atualizamos visualmente também
                  if (treatPricesRef.current) treatPricesRef.current.value = "Os valores dos tratamentos são passados somente mediante avaliação";
                } else {
                  setTreatmentPrices("");
                  if (treatPricesRef.current) treatPricesRef.current.value = "";
                }
              }}
              className="w-4 h-4 text-amber-500 rounded border-gray-300 focus:ring-amber-500"
            />
            <span className="flex items-center gap-1.5 text-sm font-medium text-amber-800">
              Valores passados somente após avaliação
              <InfoTooltip text="Use esta opção quando a clínica não informa preço de tratamentos antes da avaliação presencial." />
            </span>
          </label>
          {pricesUnderEvaluation && (
            <p className="text-xs text-amber-600 mt-2 ml-6">
              ✓ O campo abaixo foi preenchido automaticamente e travado.
            </p>
          )}
        </div>
      )}

      <Field label="Tabela de preços dos tratamentos" hint="Liste os principais tratamentos e preços" tooltip="Valores ou faixas de preço que ajudam o assistente a responder dúvidas comerciais." required={!pricesUnderEvaluation}>
        <Textarea
          ref={treatPricesRef}
          rows={5}
          required={!pricesUnderEvaluation}
          disabled={pricesUnderEvaluation}
          placeholder="Ex: Implante: a partir de R$ 2.500,00&#10;Clareamento: a partir de R$ 800,00&#10;Lentes: a partir de R$ 1.200,00 por dente"
          defaultValue={treatmentPrices}
          onChange={persistOnChange ? (e) => setTreatmentPrices(e.target.value) : undefined}
          onBlur={() => treatPricesRef.current && setTreatmentPrices(treatPricesRef.current.value)}
          className={pricesUnderEvaluation ? '!bg-gray-200 !text-gray-500 cursor-not-allowed border-gray-300' : ''}
        />
      </Field>

      {/* Dica com cores da empresa (mesmo padrão dos outros componentes) */}
      <div className={`rounded-2xl border p-4 ${isDark
        ? 'border-sminy-primary/30 bg-sminy-primary/10'
        : 'border-sminy-primary/20 bg-sminy-primary/5'
        }`}>
        <div className="flex gap-3">
          <HelpCircle className="w-5 h-5 text-sminy-primary flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-sminy-primary mb-1">Dica sobre preços</h4>
            <p className={`text-sm ${isDark ? 'text-sminy-primary/90' : 'text-sminy-primary/80'
              }`}>
              Recomenda-se usar faixas de preço ou valores "a partir de", explicando que o valor final depende de uma avaliação presencial.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialConfigForm;
